import * as React from "react";
import { Slider as SliderPrimitive } from "@base-ui/react/slider";

import { cn } from "../../lib/utils";

/**
 * Narrower than the primitive on purpose — both narrowings make a path that has
 * never worked in this component unrepresentable rather than merely documented.
 *
 * `Value` is a one-element tuple, not `readonly number[]`. This component
 * renders a single `<Thumb />` instead of one per value, so passing two values
 * has always produced a broken slider: under Radix one handle with the fill
 * drawn between both values, under Base UI one handle with no fill at all.
 * Neither is a range slider. The tuple turns that into a compile error at the
 * call site. Making it actually work means rendering one `Thumb` per value the
 * way upstream shadcn now does — a real feature, owed its own PR.
 *
 * `orientation` is omitted for the same reason: the classes below are
 * unconditionally horizontal (`flex items-center`, `h-2 w-full`), so
 * `orientation="vertical"` never laid out vertically under either primitive. A
 * prop that is accepted and silently ignored is worse than one that isn't there.
 */
type SliderProps = Omit<
  SliderPrimitive.Root.Props<number | readonly [number]>,
  "orientation"
> & {
  ref?: React.Ref<HTMLDivElement>;
};

/**
 * Migrated from `@radix-ui/react-slider` to Base UI.
 *
 * Base UI's anatomy adds a `Control` part between `Root` and `Track`: `Root` is
 * now only the value owner, and `Control` is the surface that takes pointer
 * events — the job Radix's `Root` did itself. So the layout classes that were
 * on the Radix root moved to `Control`, and the caller's `className` stays on
 * `Root`, which is still the outermost element. `Range` is called `Indicator`
 * here; same role, same classes.
 *
 * `thumbAlignment="edge"` is not a style choice. Base UI defaults to `center`,
 * which lets the thumb hang half outside the track at min and max; Radix
 * effectively inset it. Upstream shadcn's Base UI slider sets `edge` for the
 * same reason, and our AtMinimum/AtMaximum screenshot baselines pin it.
 *
 * Deliberately *not* changed here: upstream now renders one `Thumb` per value,
 * which would make range sliders work for the first time. This component has
 * always rendered a single thumb, so that stays — fixing it is a behaviour
 * change and belongs in its own PR, not hidden inside a migration diff. See
 * `SliderProps` for how the broken path is kept out of callers' reach instead.
 */
const Slider = ({ ref, className, ...props }: SliderProps) => (
  <SliderPrimitive.Root
    ref={ref}
    thumbAlignment="edge"
    className={cn("w-full", className)}
    {...props}
  >
    <SliderPrimitive.Control className="relative flex w-full touch-none select-none items-center">
      <SliderPrimitive.Track className="relative h-2 w-full grow overflow-hidden rounded-full bg-secondary">
        <SliderPrimitive.Indicator className="absolute h-full bg-primary" />
      </SliderPrimitive.Track>
      {/*
        The focus ring is driven by `has-[:focus-visible]:`, not
        `focus-visible:`. Base UI's Thumb renders a `<div>` wrapping an
        `<input type="range">`, and focus lands on that input — so a
        `focus-visible:` class on the div never matches and the ring silently
        disappears while the slider still responds to the keyboard. `has-[…]`
        matches the div when its descendant input is focus-visible, which
        reproduces the Radix ring exactly (pinned by the slider-Default-focused
        baseline). Upstream shadcn's base variant still uses bare
        `focus-visible:` here and has the same bug.
      */}
      <SliderPrimitive.Thumb className="block h-5 w-5 rounded-full border-2 border-primary bg-background ring-offset-background transition-colors has-[:focus-visible]:outline-none has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-2 disabled:pointer-events-none disabled:opacity-50" />
    </SliderPrimitive.Control>
  </SliderPrimitive.Root>
);

Slider.displayName = "Slider";

export { Slider };
